import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { useIsPremium } from "@-/dev.improve.haniverse.components.superwall";
import {
  captureAnalyticsEvent,
  registerAnalyticsSuperProperties,
  setAnalyticsPersonProperties,
  useAnalyticsDistinctId,
} from "@-/dev.improve.haniverse.lib.analytics";
import { useLocalAccount } from "@-/dev.improve.haniverse.lib.auth.client";
import { findCurrentLevelIndex } from "@-/dev.improve.haniverse.lib.cards.deck.level";
import { useStudiedGraphemesQueryOptions } from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.meaning.read";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { useLevels } from "@-/dev.improve.haniverse.routes.nav.app._lib.level";
import { useEffect, useMemo, useRef } from "react";
import {
  buildAnalyticsPersonProperties,
  buildAnalyticsSuperProperties,
} from "#analytics-person-properties";
import { advanceLevelHighWaterMark } from "#level-completion-high-water-mark";

/**
 * Keeps the analytics person and super properties in step with the learner, and reports
 * `level_completed`. Renders nothing. Re-sends after every identity change, because PostHog's
 * `reset()` clears super properties; otherwise only when a value changes (each send is billed).
 */
export function AnalyticsPersonSync() {
  const distinctId = useAnalyticsDistinctId();
  const accountStatus = useLocalAccount()?.status ?? null;
  const isPremium = useIsPremium();
  const user = useActorUser();
  const [slideTextSize] = useDeviceSetting("slideTextSize");
  const { data: studiedGraphemes } = useQuery(useStudiedGraphemesQueryOptions());
  const levels = useLevels();
  const currentLevelIndex =
    studiedGraphemes === undefined ? undefined : findCurrentLevelIndex(levels, studiedGraphemes);

  const superProperties = useMemo(
    () => buildAnalyticsSuperProperties({ accountStatus, hanziScript: user.hanziScript, isPremium }),
    [accountStatus, isPremium, user.hanziScript],
  );
  const personProperties = useMemo(
    () =>
      buildAnalyticsPersonProperties({
        charactersLearned: studiedGraphemes?.size ?? null,
        currentLevelIndex,
        isPremium,
        slideTextSize,
        user,
      }),
    [currentLevelIndex, isPremium, slideTextSize, studiedGraphemes, user],
  );

  const lastSentRef = useRef<string | null>(null);
  useEffect(() => {
    if (distinctId === null) {
      return;
    }
    const payload = JSON.stringify([distinctId, superProperties, personProperties]);
    if (lastSentRef.current === payload) {
      return;
    }
    lastSentRef.current = payload;
    registerAnalyticsSuperProperties(superProperties);
    setAnalyticsPersonProperties(personProperties);
  }, [distinctId, personProperties, superProperties]);

  useEffect(() => {
    if (distinctId === null || currentLevelIndex === undefined) {
      return;
    }
    const completed = advanceLevelHighWaterMark({
      currentLevelIndex,
      distinctId,
      levelIndexes: levels.map((level) => level.index),
    });
    for (const levelIndex of completed) {
      captureAnalyticsEvent("level_completed", { level_index: levelIndex });
    }
  }, [currentLevelIndex, distinctId, levels]);

  return null;
}
